import { HttpClient } from "@angular/common/http";
import { CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { MatButton, MatIconButton } from "@angular/material/button";
import {
  MatCard,
  MatCardContent,
  MatCardHeader,
  MatCardTitle
} from "@angular/material/card";
import { MatDivider } from "@angular/material/divider";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatToolbar } from "@angular/material/toolbar";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { firstValueFrom } from "rxjs";
import { NgxEchartsDirective, provideEchartsCore } from "ngx-echarts";
import { HeatmapChart, LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  VisualMapComponent
} from "echarts/components";
import * as echarts from "echarts/core";
import type { EChartsCoreOption } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  LineChart,
  HeatmapChart,
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  LegendComponent,
  CanvasRenderer
]);

export interface PartitionMetrics {
  partitionIndex: number;
  threadName: string;
  generation: number;
  dates: string[];
  ticketCount: number;
  resourceCount: number;
  constructionSpeed: number;
  localSearchSpeed: number;
}

export interface PartitionSpeedRecord {
  timestamp: string;
  generation: number;
  speeds: { [ordinal: number]: number };
}

export interface SolverPerformanceMetrics {
  scoreCalculationCount: number;
  scoreCalculationSpeed: number;
  solveDurationSeconds: number;
  averageMovesPerStep: number;
  bestSolutionCount: number;
  lastBestSolutionTime: string | null;
  memoryUsedBytes: number;
  memoryMaxBytes: number;
  ticketCount: number;
  resourceAvailabilityCount: number;
  partitioningEnabled: boolean;
  partitionCount: number;
  uptime: string;
  solverStatus: string;
  partitions: PartitionMetrics[];
  allDates: string[];
  generationHistory: PartitionMetrics[][];
  partitionSpeedHistory: PartitionSpeedRecord[];
}

interface CoreSizeStat {
  coreLabel: string;
  tickets: number;
  resources: number;
}

@Component({
  selector: "app-stats",
  templateUrl: "./stats.component.html",
  styleUrls: ["./stats.component.scss"],
  imports: [
    CommonModule,
    DecimalPipe,
    DatePipe,
    MatToolbar,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardTitle,
    MatIcon,
    MatButton,
    MatIconButton,
    MatProgressSpinner,
    MatDivider,
    NgxEchartsDirective
  ],
  providers: [provideEchartsCore({ echarts })]
})
export class StatsComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private clientConfigService = inject(ClientConfigService);

  metrics: SolverPerformanceMetrics | null = null;
  loading = true;
  error: string | null = null;
  lastRefreshed: Date | null = null;
  autoRefresh = true;
  refreshInterval = 5000;
  chartTimeWindowMinutes = 10;
  maximizedCard: "speed" | "heatmap" | null = null;

  readonly TIME_WINDOW_OPTIONS = [
    { label: "10m", minutes: 10 },
    { label: "30m", minutes: 30 },
    { label: "1h", minutes: 60 },
    { label: "2h", minutes: 120 },
    { label: "4h", minutes: 240 },
    { label: "8h", minutes: 480 },
    { label: "12h", minutes: 720 }
  ];

  speedChartOptions: EChartsCoreOption = {};
  heatmapOptions: EChartsCoreOption = {};

  private intervalId: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.fetchMetrics();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  async fetchMetrics(): Promise<void> {
    this.loading = !this.metrics;
    this.error = null;
    try {
      const config = await this.clientConfigService.config;
      this.metrics = await firstValueFrom(
        this.http.get<SolverPerformanceMetrics>(
          config.serviceUrl + "/api/v1/solver/metrics",
          { withCredentials: true }
        )
      );
      this.lastRefreshed = new Date();
      this.rebuildCharts();
    } catch (e: any) {
      this.error = e.status
        ? `HTTP ${e.status}: ${e.statusText}`
        : e.message || "Failed to fetch metrics";
    }
    this.loading = false;
  }

  toggleAutoRefresh(): void {
    this.autoRefresh = !this.autoRefresh;
    if (this.autoRefresh) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  setChartTimeWindow(minutes: number): void {
    this.chartTimeWindowMinutes = minutes;
    this.rebuildCharts();
  }

  toggleMaximize(card: "speed" | "heatmap"): void {
    this.maximizedCard = this.maximizedCard === card ? null : card;
  }

  closeMaximize(): void {
    this.maximizedCard = null;
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  }

  getMemoryPercent(): number {
    if (!this.metrics || this.metrics.memoryMaxBytes === 0) return 0;
    return (this.metrics.memoryUsedBytes / this.metrics.memoryMaxBytes) * 100;
  }

  getStatusColor(): string {
    if (!this.metrics) return "";
    switch (this.metrics.solverStatus) {
      case "SOLVING":
        return "status-solving";
      case "SLEEPING":
        return "status-sleeping";
      default:
        return "status-other";
    }
  }

  getCoreSizeStats(): CoreSizeStat[] {
    if (!this.metrics) return [];

    const current = [...(this.metrics.partitions ?? [])].sort(
      (a, b) => a.partitionIndex - b.partitionIndex
    );
    const last = [...this.getLastGenerationPartitions()].sort(
      (a, b) => a.partitionIndex - b.partitionIndex
    );
    const coreCount = Math.max(current.length, last.length);
    if (coreCount === 0) return [];

    return Array.from({ length: coreCount }, (_, coreOrdinal) => {
      const currentPart =
        coreOrdinal < current.length ? current[coreOrdinal] : null;
      const lastPart = coreOrdinal < last.length ? last[coreOrdinal] : null;
      const ticketCount =
        currentPart?.ticketCount ?? lastPart?.ticketCount ?? 0;
      const resourceCount =
        currentPart?.resourceCount ?? lastPart?.resourceCount ?? 0;
      return {
        coreLabel: `Core ${coreOrdinal + 1}`,
        tickets: ticketCount,
        resources: resourceCount
      };
    });
  }

  hasSpeedChartData(): boolean {
    return this.getFilteredHistory().length > 1;
  }

  hasHeatmapData(): boolean {
    const dates = this.metrics?.allDates ?? [];
    const coreCount = this.getCoreCount();
    const current = this.metrics?.partitions ?? [];
    const last = this.getLastGenerationPartitions();
    const hasCoverage =
      current.some((p) => (p.dates?.length ?? 0) > 0) ||
      last.some((p) => (p.dates?.length ?? 0) > 0);
    return dates.length > 0 && coreCount > 0 && hasCoverage;
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.intervalId = setInterval(
      () => this.fetchMetrics(),
      this.refreshInterval
    );
  }

  private stopAutoRefresh(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private getCoreCount(): number {
    const current = this.metrics?.partitions?.length ?? 0;
    const last = this.getLastGenerationPartitions().length;
    return Math.max(current, last);
  }

  private getLastGenerationPartitions(): PartitionMetrics[] {
    return this.metrics?.generationHistory?.[0] ?? [];
  }

  private getFilteredHistory(): {
    time: Date;
    speeds: { [index: number]: number };
  }[] {
    const records = this.metrics?.partitionSpeedHistory ?? [];
    const mapped = records.map((r) => ({
      time: new Date(r.timestamp),
      speeds: r.speeds
    }));
    if (mapped.length === 0) return [];
    const cutoff = Date.now() - this.chartTimeWindowMinutes * 60 * 1000;
    const filtered = mapped.filter((s) => s.time.getTime() >= cutoff);
    return filtered.length > 0 ? filtered : mapped;
  }

  private rebuildCharts(): void {
    this.buildSpeedChart();
    this.buildHeatmapChart();
  }

  private buildSpeedChart(): void {
    const history = this.getFilteredHistory();
    const partitions = this.getActivePartitionIndices(history);

    this.speedChartOptions = {
      animationDuration: 500,
      animationDurationUpdate: 450,
      animationEasing: "cubicOut",
      animationEasingUpdate: "cubicOut",
      grid: { left: 52, right: 18, top: 24, bottom: 36 },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" }
      },
      legend: {
        top: 0,
        textStyle: { fontSize: 11 }
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: history.map((h) => this.formatTime(h.time)),
        axisLabel: { fontSize: 11, color: "#6d7588" }
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: "#6d7588",
          formatter: (value: number) => this.formatSpeed(value)
        },
        splitLine: { lineStyle: { color: "#e4e8f0", type: "dashed" } }
      },
      series: partitions.map((idx) => ({
        name: `Core ${idx + 1}`,
        type: "line",
        smooth: true,
        showSymbol: true,
        symbol: "circle",
        symbolSize: 7,
        lineStyle: { width: 2.5 },
        emphasis: { focus: "series" },
        data: history.map((snapshot) => snapshot.speeds[idx] ?? null)
      }))
    };
  }

  private buildHeatmapChart(): void {
    const dates = this.metrics?.allDates ?? [];
    const coreCount = this.getCoreCount();
    const current = [...(this.metrics?.partitions ?? [])].sort(
      (a, b) => a.partitionIndex - b.partitionIndex
    );
    const last = [...this.getLastGenerationPartitions()].sort(
      (a, b) => a.partitionIndex - b.partitionIndex
    );

    const heatRows = Array.from(
      { length: coreCount },
      (_, i) => `Core ${i + 1}`
    );
    const data: any[] = [];

    for (let core = 0; core < coreCount; core++) {
      for (let dateIndex = 0; dateIndex < dates.length; dateIndex++) {
        const date = dates[dateIndex];
        const currentPart = core < current.length ? current[core] : null;
        const lastPart = core < last.length ? last[core] : null;
        const currentHasDate = currentPart?.dates?.includes(date) ?? false;
        const lastHasDate = lastPart?.dates?.includes(date) ?? false;
        const speed =
          lastHasDate && lastPart
            ? (lastPart.localSearchSpeed > 0
                ? lastPart.localSearchSpeed
                : lastPart.constructionSpeed) || 0
            : 0;

        data.push({
          value: [dateIndex, core, speed],
          currentHasDate,
          lastHasDate,
          currentTickets: currentPart?.ticketCount ?? 0,
          currentResources: currentPart?.resourceCount ?? 0,
          lastTickets: lastPart?.ticketCount ?? 0,
          lastResources: lastPart?.resourceCount ?? 0,
          generation: lastPart?.generation ?? 0
        });
      }
    }

    const showCellLabels = coreCount * Math.max(1, dates.length) <= 120;

    this.heatmapOptions = {
      animationDuration: 550,
      animationDurationUpdate: 450,
      grid: { left: 74, right: 22, top: 26, bottom: 64 },
      tooltip: {
        position: "top",
        formatter: (params: any) => {
          const point = params.data;
          const date = dates[point.value[0]];
          const core = heatRows[point.value[1]];
          const speedLabel = point.lastHasDate
            ? `${this.formatSpeed(point.value[2])}/sec`
            : "No last generation data";
          const currentLabel = point.currentHasDate
            ? `Current: ${point.currentTickets} tickets, ${point.currentResources} resources`
            : "Current: not assigned";
          const lastLabel = point.lastHasDate
            ? `Last gen ${point.generation}: ${point.lastTickets} tickets, ${point.lastResources} resources`
            : "Last gen: not assigned";
          return `${core}<br/>${date}<br/>${speedLabel}<br/>${currentLabel}<br/>${lastLabel}`;
        }
      },
      xAxis: {
        type: "category",
        data: dates.map((d) => this.formatDateLabel(d)),
        splitArea: { show: true },
        axisLabel: { fontSize: 11, color: "#6d7588", rotate: 32, margin: 12 }
      },
      yAxis: {
        type: "category",
        data: heatRows,
        inverse: true,
        splitArea: { show: true },
        axisLabel: { fontSize: 12, color: "#6d7588", margin: 14 }
      },
      visualMap: {
        min: 0,
        max: this.getHeatmapMax(data),
        calculable: false,
        orient: "horizontal",
        left: "center",
        bottom: 0,
        textStyle: { fontSize: 10 },
        inRange: {
          color: ["#f5f7fb", "#d7e8ff", "#9fc5ff", "#5f93ff", "#2f67db"]
        }
      },
      series: [
        {
          name: "Core Date Heat",
          type: "heatmap",
          data,
          label: {
            show: showCellLabels,
            formatter: (p: any) =>
              p.data?.value?.[2] > 0 ? this.formatSpeed(p.data.value[2]) : "",
            fontSize: 10,
            color: "#0f2b56"
          },
          itemStyle: {
            borderWidth: 1,
            borderColor: "#d9dfeb"
          },
          emphasis: {
            itemStyle: {
              borderColor: "#1e5dc8",
              borderWidth: 2
            }
          }
        }
      ]
    };
  }

  private getHeatmapMax(data: any[]): number {
    const max = Math.max(...data.map((d) => d.value[2]), 1);
    return Math.max(1, Math.ceil(max));
  }

  private getActivePartitionIndices(
    history: { time: Date; speeds: { [index: number]: number } }[]
  ): number[] {
    const indices = new Set<number>();
    for (const snapshot of history) {
      for (const key of Object.keys(snapshot.speeds)) {
        indices.add(Number(key));
      }
    }
    return Array.from(indices).sort((a, b) => a - b);
  }

  private formatDateLabel(isoDate: string): string {
    const d = new Date(isoDate + "T00:00:00");
    const day = d.toLocaleDateString(undefined, { weekday: "short" });
    return `${day} ${d.getDate()}`;
  }

  private formatTime(time: Date): string {
    return `${time.getHours().toString().padStart(2, "0")}:${time
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }

  private formatSpeed(speed: number): string {
    if (speed >= 1000000) return (speed / 1000000).toFixed(1) + "M";
    if (speed >= 1000) return (speed / 1000).toFixed(0) + "k";
    return speed.toFixed(0);
  }
}
